import { Action, Selector, State, StateContext } from "@ngxs/store";
import { IAuthStateModel } from "../../models/auth.model";
import { Injectable, inject } from "@angular/core";
import { Auth } from "./auth.action";
import { Navigate } from '@ngxs/router-plugin';
import { AuthService } from "../../services/auth/auth.service";
import { IKeycloakTokenResponse } from "../../models/keycloak-token-detail.model";
import { catchError, forkJoin, iif, of, tap, throwError } from "rxjs";
import { TokenRefreshService } from "../../services/auth/token-refresh.service";
import { SESSION_TIMEOUT } from "../../constants/system-constants.model";
import { LOCAL_STORAGE } from "../../constants/localstorage-enum";
import { KeycloakGroup } from "../../../core/modules/admin-modules/administrator/role-and-permission/models/keycloak-group.model";
import { RolePermissionService } from "../../../core/modules/admin-modules/administrator/role-and-permission/role-permission.service";
import { environment } from "../../../../environments/environment";
import { ProviderService } from "../../../core/modules/admin-modules/provider/service/provider.service";
import { PayerService } from "../../../core/modules/admin-modules/insurance-company/service/payer.service";
import { BroadCastChannelService } from "../../services/broadcast/broad-cast-channel.service";
import { BroadcastEventType, StoreKeys } from "../../models/broadcast.model";
import { UserType } from "../../constants/user-type-enum";
import { HttpErrorResponse } from "@angular/common/http";
import { ILoggedInUser } from "../../models/logged-in-user.model";
import { SignalrService } from "../../services/common/signalR.service";
import { PatientService } from "../../../core/modules/patient-modules/service/patient.service";
import { LanguageType } from "../../constants/language-eum";
import { LanguageService } from "../../services/translation/language.service";
import { SettingsName } from "../../constants/settings-type-enum";
import { SettingService } from "../../services/settings/setting.service";
import { SuperSetService } from "../../services/super-set/super-set.service";

const initialAuthState: IAuthStateModel = {
    authDetails: null,
    currentUser: null,
    permissions: null,
    role: null,
    userType: null,
    providers: null,
    payers: null,
    patient: null,
    userSettings: null,
    chartSettings: null,
    payerSettings: null
};
@State<IAuthStateModel>({
    name: "auth",
    defaults: initialAuthState
})

@Injectable()
export class AuthState {
    private authService = inject(AuthService);
    private tokenRefreshService = inject(TokenRefreshService);
    private providerService = inject(ProviderService);
    private payerService = inject(PayerService);
    private patientService = inject(PatientService);
    private rolePermissionService = inject(RolePermissionService);
    private broadcastService = inject(BroadCastChannelService);
    private signalRService = inject(SignalrService);
    private languageService = inject(LanguageService);
    private settingService = inject(SettingService);
    private superSetService = inject(SuperSetService);

    @Selector()
    static token(state: IAuthStateModel) {
        return state?.authDetails?.access_token;
    }

    @Selector()
    static tokenExpiryAt(state: IAuthStateModel) {
        return state?.authDetails?.expires_in;
    }

    @Selector()
    static refreshToken(state: IAuthStateModel) {
        return state?.authDetails?.refresh_token;
    }

    @Selector()
    static refreshTokenExpiryAt(state: IAuthStateModel) {
        return state?.authDetails?.refresh_expires_in;
    }

    @Selector()
    static permissions(state: IAuthStateModel) {
        return state?.permissions;
    }

    @Selector()
    static role(state: IAuthStateModel) {
        return state?.role;
    }

    @Selector()
    static currentUser(state: IAuthStateModel) {
        return state?.currentUser;
    }

    @Selector()
    static userType(state: IAuthStateModel) {
        return state?.userType;
    }

    @Selector()
    static providerIds(state: IAuthStateModel) {
        return state?.providers;
    }

    @Selector()
    static payerIds(state: IAuthStateModel) {
        return state?.payers;
    }

    @Selector()
    static username(state: IAuthStateModel) {
        return state?.currentUser?.username;
    }

    @Selector()
    static userId(state: IAuthStateModel) {
        return state?.currentUser?.id;
    }

    @Selector()
    static patientId(state: IAuthStateModel) {
        return state?.patient;
    }

    @Selector()
    static email(state: IAuthStateModel) {
        return state?.currentUser?.email;
    }

    @Selector()
    static checkForPayerAndProviderAccess(state: IAuthStateModel) {
        return !(UserType.TPA == state?.userType && (state.providers == null || state.providers?.length == 0) && (state.payers == null || state.payers?.length == 0));
    }

    @Selector()
    static isTPAUser(state: IAuthStateModel) {
        return UserType.TPA == state?.userType;
    }

    @Selector()
    static userSettings(state: IAuthStateModel) {
        return state?.userSettings;
    }

    @Selector()
    static chartSettings(state: IAuthStateModel) {
        return state?.chartSettings;
    }

    @Selector()
    static payerSettings(state: IAuthStateModel) {
        return state?.payerSettings;
    }

    @Selector()
    static isPatientUser(state: IAuthStateModel) {
        return UserType.Patient == state?.userType;
    }

    @Selector()
    static fullName(state: IAuthStateModel) {
        const { firstName, lastName } = state?.currentUser ?? {};
        return firstName ? `${firstName} ${lastName ?? ''}`.trim() : lastName || null;
    }

    @Selector()
    static isLoggedIn(state: IAuthStateModel) {
        const expirationTime = state?.authDetails?.refreshExpirationTime;
        const currentDateTime = new Date().getTime();
        return state && !!state.authDetails?.access_token && expirationTime && (currentDateTime < expirationTime);
    }

    @Action(Auth.Login)
    login(ctx: StateContext<IAuthStateModel>, { payload }: Auth.Login) {
        this.authService.login(payload).subscribe({
            next: (response: IKeycloakTokenResponse) => {
                const expirationTime = new Date().getTime() + response.expires_in * 1000;
                const refreshExpirationTime = new Date().getTime() + response.refresh_expires_in * 1000;
                const decodedToken = this.authService.decodeToken(response.access_token);
                const authState: IAuthStateModel = {
                    authDetails: response,
                    currentUser: null,
                    permissions: null,
                    role: null,
                    userType: null,
                    providers: null,
                    payers: null,
                    patient: null,
                    userSettings: null,
                    chartSettings: null,
                    payerSettings: null
                };

                authState.authDetails.expirationTime = expirationTime;
                authState.authDetails.refreshExpirationTime = refreshExpirationTime;
                localStorage.setItem(LOCAL_STORAGE.AUTH_DETAILS, JSON.stringify(authState.authDetails));
                localStorage.setItem(LOCAL_STORAGE.CURRENT_USER, JSON.stringify(authState.currentUser));
                ctx.patchState(authState);

                this.authService.getUserByUsername(payload.email).subscribe({
                    next: (userInfo: ILoggedInUser) => {
                        const user = userInfo;
                        authState.currentUser = {
                            id: user.id,
                            email: user.email,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            username: user.username,
                            emailVerified: user.emailVerified,
                            createdTimestamp: user.createdTimestamp,
                            enabled: user.enabled,
                            totp: user.totp,
                            disableableCredentialTypes: user.disableableCredentialTypes,
                            requiredActions: user.requiredActions,
                            notBefore: user.notBefore,
                            access: user.access,
                            attributes: user.attributes,
                            groups: user.groups
                        };
                        localStorage.setItem(LOCAL_STORAGE.CURRENT_USER, JSON.stringify(authState.currentUser));
                        authState.userType = decodedToken.userType;
                        authState.providers = decodedToken.providers;
                        authState.payers = decodedToken.payers;
                        authState.patient = decodedToken.patient;
                        ctx.patchState(authState);

                        // Fetch provider/Payer details in the background
                        this.providerService.setStoreProviders(authState.providers);
                        this.payerService.setStorePayers(authState.payers);

                        iif(() => user.groups.length > 0, this.rolePermissionService.getGroupById(user.groups[0].id), of(null))
                            .subscribe({
                                next: (group: KeycloakGroup) => {
                                    if (group && group.clientRoles && group.clientRoles[environment.keyCloakConfig.authClientId]) {
                                        authState.role = group;
                                        authState.permissions = group.clientRoles[environment.keyCloakConfig.authClientId].map(item => item.name);
                                        ctx.patchState(authState);
                                        ctx.dispatch(new Auth.LoginSuccess(authState));
                                    } else
                                        console.error('User permission not found');
                                },
                                error: (error: HttpErrorResponse) => console.error('Error while fetching user group info', error)
                            });
                    },
                    error: (error: HttpErrorResponse) => console.error('Error while fetching user info', error)
                });
            },
            error: (errorResp: HttpErrorResponse) => ctx.dispatch(new Auth.LoginFailed(errorResp.error?.error_description, false))
        });
    }

    @Action(Auth.PatientLogin)
    patientLogin(ctx: StateContext<IAuthStateModel>, { payload }: Auth.PatientLogin) {
        payload.otpCode = payload.otpCode.toString();
        this.authService.patientLogin(payload).subscribe({
            next: (response: IKeycloakTokenResponse) => {
                const expirationTime = new Date().getTime() + response.expires_in * 1000;
                const refreshExpirationTime = new Date().getTime() + response.refresh_expires_in * 1000;
                const decodedToken = this.authService.decodeToken(response.access_token);
                const authState: IAuthStateModel = {
                    authDetails: response,
                    currentUser: null,
                    permissions: null,
                    role: null,
                    userType: null,
                    providers: null,
                    payers: null,
                    patient: null,
                    userSettings: null,
                    chartSettings: null,
                    payerSettings: null
                };

                authState.authDetails.expirationTime = expirationTime;
                authState.authDetails.refreshExpirationTime = refreshExpirationTime;
                localStorage.setItem(LOCAL_STORAGE.AUTH_DETAILS, JSON.stringify(authState.authDetails));
                localStorage.setItem(LOCAL_STORAGE.CURRENT_USER, JSON.stringify(authState.currentUser));
                ctx.patchState(authState);

                this.authService.getUserByUsername(decodedToken.email).subscribe({
                    next: (userInfo: ILoggedInUser) => {
                        const user = userInfo;
                        authState.currentUser = {
                            id: user.id,
                            email: user.email,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            username: user.username,
                            emailVerified: user.emailVerified,
                            createdTimestamp: user.createdTimestamp,
                            enabled: user.enabled,
                            totp: user.totp,
                            disableableCredentialTypes: user.disableableCredentialTypes,
                            requiredActions: user.requiredActions,
                            notBefore: user.notBefore,
                            access: user.access,
                            attributes: user.attributes,
                            groups: user.groups
                        };
                        localStorage.setItem(LOCAL_STORAGE.CURRENT_USER, JSON.stringify(authState.currentUser));
                        authState.userType = decodedToken.userType;
                        authState.patient = decodedToken.patient;
                        ctx.patchState(authState);

                        if (authState.patient) this.patientService.setStorePatient(authState.patient);

                        iif(() => user.groups.length > 0, this.rolePermissionService.getGroupById(user.groups[0].id), of(null))
                            .subscribe({
                                next: (group: KeycloakGroup) => {
                                    if (group && group.clientRoles && group.clientRoles[environment.keyCloakConfig.authClientId]) {
                                        authState.role = group;
                                        authState.permissions = group.clientRoles[environment.keyCloakConfig.authClientId].map(item => item.name);
                                        ctx.patchState(authState);
                                        ctx.dispatch(new Auth.LoginSuccess(authState));
                                    } else
                                        console.error('User permission not found');
                                },
                                error: (error: HttpErrorResponse) => console.error('Error while fetching user group info', error)
                            });
                    },
                    error: (error: HttpErrorResponse) => console.error('Error while fetching user info', error)
                });
            },
            error: (errorResp: HttpErrorResponse) => ctx.dispatch(new Auth.LoginFailed(errorResp.error?.error_description, true))
        });
    }

    @Action(Auth.Logout)
    logout(ctx: StateContext<IAuthStateModel>, { isPatientUser, sessionTimeout }: Auth.Logout) {
        this.authService.logout(sessionTimeout).subscribe();
        return ctx.dispatch(new Auth.LogoutSuccess(isPatientUser));
    }

    @Action(Auth.SetUserSettings)
    setUserSettings(ctx: StateContext<IAuthStateModel>, { userSettings }: Auth.SetUserSettings) {
        return ctx.patchState({ userSettings: userSettings });
    }

    @Action(Auth.SetChartSettings)
    setChartSettings(ctx: StateContext<IAuthStateModel>, { chartSettings }: Auth.SetChartSettings) {
        return ctx.patchState({ chartSettings: chartSettings });
    }

    @Action(Auth.SetPayerSettings)
    setPayerSettings(ctx: StateContext<IAuthStateModel>, { payerSettings }: Auth.SetPayerSettings) {
        return ctx.patchState({ payerSettings: payerSettings });
    }

    @Action(Auth.LoginRedirect)
    onLoginRedirect(ctx: StateContext<IAuthStateModel>, { isPatientUser }: Auth.LoginRedirect) {
        this.authService.removeItemsFromLocalStoage(true);
        this.superSetService.chartTokenCache.clear();
        this.tokenRefreshService.stopTimer();
        this.signalRService.stopConnection();
        this.broadcastService.broadcastState(StoreKeys.ALL, isPatientUser);
        this.broadcastService.broadCastEvent(BroadcastEventType.LOGOUT, isPatientUser);
        ctx.dispatch(new Navigate([this.authService.getLoginPagePath(isPatientUser)]));
    }

    @Action(Auth.LoginSuccess)
    onLoginSuccess(ctx: StateContext<IAuthStateModel>, payload: Auth.LoginSuccess) {
        const userId = payload.authDetails.currentUser.id;
        const userType = payload.authDetails.userType;
        this.authService.clearPageAccessCache();
        forkJoin({
            userSettings: this.settingService.getAllSettings(userId, true),
            chartSettings: this.settingService.getChartSettings(userId, true, true),
            payerSettings: iif(() => [UserType.Payer, UserType.TPA].includes(userType), this.settingService.getPayerSettings(), of(null))
        }).subscribe({
            next: ({ userSettings, chartSettings, payerSettings }) => {
                payload.authDetails.userSettings = userSettings;
                payload.authDetails.chartSettings = chartSettings;
                payerSettings && (payload.authDetails.payerSettings = payerSettings);
                this.setDetailsAndRedirectUser(ctx, payload.authDetails);
            },
            error: () => {
                this.setDetailsAndRedirectUser(ctx, payload.authDetails);
            }
        });
    }

    @Action(Auth.ResetState)
    resetState(ctx: StateContext<IAuthStateModel>) {
        this.authService.logout(true).subscribe(() => {
            this.resetAuthState(ctx);
            this.authService.removeItemsFromLocalStoage(true);
        });
    }

    @Action([Auth.LoginFailed, Auth.LogoutSuccess])
    setUserStateOnFailure(ctx: StateContext<IAuthStateModel>, { isPatientUser }: Auth.LoginFailed | Auth.LogoutSuccess) {
        this.resetAuthState(ctx);
        ctx.dispatch(new Auth.LoginRedirect(isPatientUser));
    }

    @Action(Auth.RefreshToken)
    refreshToken(ctx: StateContext<IAuthStateModel>) {
        return this.authService.refreshToken().pipe(
            tap((response: IKeycloakTokenResponse) => {
                const expirationTime = new Date().getTime() + response.expires_in * 1000;
                const refreshExpirationTime = new Date().getTime() + response.refresh_expires_in * 1000;
                const updatedAuthDetails = {
                    ...ctx.getState().authDetails,
                    access_token: response.access_token,
                    refresh_token: response.refresh_token,
                    expires_in: response.expires_in,
                    refresh_expires_in: response.refresh_expires_in
                };
                updatedAuthDetails.expirationTime = expirationTime;
                updatedAuthDetails.refreshExpirationTime = refreshExpirationTime;
                localStorage.setItem(LOCAL_STORAGE.AUTH_DETAILS, JSON.stringify(updatedAuthDetails));
                ctx.patchState({ authDetails: updatedAuthDetails });
                this.broadcastService.broadcastState(StoreKeys.AUTH);
                this.broadcastService.broadCastEvent(BroadcastEventType.CONTINUE);
                this.tokenRefreshService.resetTimer();
                this.tokenRefreshService.setSessionTimeout(SESSION_TIMEOUT.CONTINUE);
                this.signalRService.startConnection();
            }),
            catchError((errorResp) => throwError(errorResp))
        );
    }

    resetAuthState(ctx: StateContext<IAuthStateModel>) {
        ctx.patchState(initialAuthState);
        this.providerService.clearStore();
        this.payerService.clearStore();
        this.authService.clearPageAccessCache();
        this.patientService.clearStore();
    }

    setDetailsAndRedirectUser(ctx: StateContext<IAuthStateModel>, authDetails: IAuthStateModel) {
        ctx.patchState(authDetails);
        let isDifferentLanguage = false;
        const languageSetting = authDetails.userSettings?.find(item => item.settingName == SettingsName.LanguagePreference);
        if (languageSetting) {
            const valueFromSetting = this.settingService.getValueFromSetting(languageSetting);
            if (valueFromSetting) {
                const languageFromSetting = LanguageType.getLanguageCode(valueFromSetting)?.toLowerCase();
                const languageFromStorage = ((localStorage.getItem(LOCAL_STORAGE.CURRENT_LANGUAGE) ? <LanguageType>localStorage.getItem(LOCAL_STORAGE.CURRENT_LANGUAGE) : LanguageType.English).toString())?.toLowerCase();
                isDifferentLanguage = languageFromSetting != LanguageType.getLanguageCode(languageFromStorage);
            }
        }
        this.tokenRefreshService.resetTimer();
        const url = localStorage.getItem(LOCAL_STORAGE.LAST_URL);
        const isPatientUser = authDetails.userType === UserType.Patient;
        this.signalRService.startConnection();
        this.broadcastService.broadcastState(StoreKeys.ALL, isPatientUser);
        this.broadcastService.broadCastEvent(BroadcastEventType.LOGIN, isPatientUser);
        ctx.dispatch(new Navigate([url ? url : this.authService.getDashboardPath(isPatientUser)]));
        isDifferentLanguage && this.languageService.setReloadWindow(true);
    }

}
